import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Megaphone, Star, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { GuestPrice } from '@/components/GuestPrice';

/* ─── Auto-Playing Carousel — fetches ads for a specific section ─── */

export function AdCarousel({ section = 'feed' }) {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    axios.get(`${API}/ads/section/${section}?format=carousel`)
      .then(res => setAds(res.data || []))
      .catch(() => {});
  }, [section]);

  const next = useCallback(() => {
    setCurrent(prev => (prev + 1) % (ads.length || 1));
  }, [ads.length]);

  useEffect(() => {
    if (paused || ads.length <= 1) return;
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [paused, next, ads.length]);

  if (!ads.length) return null;
  const ad = ads[current];

  return (
    <div
      className="mb-4 rounded-2xl overflow-hidden relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="ad-carousel"
    >
      <div className="relative h-36 sm:h-44 overflow-hidden">
        <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover transition-all duration-700" loading="lazy" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.background = `linear-gradient(135deg, ${ad.color || '#2F80ED'}, #0A2540)`; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-md">
          <Megaphone className="h-2.5 w-2.5 text-white/80" />
          <span className="text-[9px] font-bold text-white/80 uppercase tracking-wider">Sponsored</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3.5">
          <h3 className="text-white font-bold text-sm leading-tight">{ad.title}</h3>
          <p className="text-white/70 text-[11px] mt-0.5">{ad.subtitle}</p>
          <button onClick={() => navigate(ad.cta_link || '/')} className="mt-2 px-4 py-1.5 rounded-full text-[11px] font-bold text-white flex items-center gap-1.5 active:scale-95 transition-all" style={{ backgroundColor: ad.color || '#2F80ED' }} data-testid={`ad-cta-${ad.ad_id}`}>
            {ad.cta_text || 'Learn More'} <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
      {ads.length > 1 && (
        <div className="absolute bottom-2.5 right-3 flex gap-1">
          {ads.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)} className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? 'w-4 bg-white' : 'w-1.5 bg-white/40'}`} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Sponsored Card — blends into the grid ─── */

export function SponsoredCard({ section = 'colleges', index = 0 }) {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);

  useEffect(() => {
    axios.get(`${API}/ads/section/${section}?format=sponsored_card`)
      .then(res => setAds(res.data || []))
      .catch(() => {});
  }, [section]);

  if (!ads.length) return null;
  const ad = ads[index % ads.length];

  return (
    <div
      className="rounded-2xl overflow-hidden bg-white border border-blue-100 shadow-sm hover:shadow-md transition-all cursor-pointer group relative"
      onClick={() => navigate(ad.cta_link || '/')}
      data-testid={`sponsored-card-${ad.ad_id}`}
    >
      {/* Image */}
      <div className="h-28 sm:h-36 w-full relative overflow-hidden">
        <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.background = `linear-gradient(135deg, ${ad.color || '#2F80ED'}, #0A2540)`; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

        {/* Sponsored badge */}
        <div className="absolute top-1.5 left-1.5 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-blue-500/90 backdrop-blur-sm">
          <Megaphone className="h-2 w-2 text-white" />
          <span className="text-[8px] font-bold text-white uppercase">Ad</span>
        </div>

        {/* Special badge */}
        {ad.badge_text && (
          <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-amber-400 text-[8px] font-bold text-amber-900">
            {ad.badge_text}
          </div>
        )}

        {/* Name overlay */}
        <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-end gap-1.5">
          {ad.logo_url && (
            <img src={ad.logo_url} alt="" className="w-7 h-7 rounded-lg border border-white/60 object-cover bg-white shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
          )}
          <div className="min-w-0">
            <h3 className="text-white font-bold text-xs leading-tight line-clamp-1 drop-shadow-sm">{ad.institution_name || ad.title}</h3>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-2 sm:px-2.5 py-2">
        <p className="text-[11px] font-bold text-[#0A2540] line-clamp-1">{ad.title}</p>
        <p className="text-[10px] text-slate-400 line-clamp-1 mt-0.5">{ad.subtitle}</p>
        <div className="flex items-center justify-between mt-1.5">
          {ad.price_label && <GuestPrice><span className="text-[11px] font-bold text-[#2F80ED]">{ad.price_label}</span></GuestPrice>}
          <span className="text-[10px] font-bold text-white px-2 py-0.5 rounded-full" style={{ backgroundColor: ad.color || '#2F80ED' }}>
            {ad.cta_text || 'View'}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Featured Banner — full-width between rows ─── */

export function FeaturedBanner({ section = 'colleges', index = 0 }) {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);

  useEffect(() => {
    axios.get(`${API}/ads/section/${section}?format=featured_banner`)
      .then(res => setAds(res.data || []))
      .catch(() => {});
  }, [section]);

  if (!ads.length) return null;
  const ad = ads[index % ads.length];

  return (
    <div
      className="col-span-2 lg:col-span-3 rounded-2xl overflow-hidden relative cursor-pointer group"
      onClick={() => navigate(ad.cta_link || '/')}
      data-testid={`featured-banner-${ad.ad_id}`}
    >
      <div className="relative h-24 sm:h-28 overflow-hidden">
        <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.background = `linear-gradient(135deg, ${ad.color || '#2F80ED'}, #0A2540)`; }} />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />

        {/* Sponsored */}
        <div className="absolute top-2 left-2.5 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/20 backdrop-blur-md">
          <Megaphone className="h-2.5 w-2.5 text-white/80" />
          <span className="text-[8px] font-bold text-white/80 uppercase tracking-wider">Sponsored</span>
        </div>

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
          <h3 className="text-white font-bold text-sm sm:text-base leading-tight">{ad.title}</h3>
          <p className="text-white/70 text-[10px] sm:text-xs mt-0.5 line-clamp-1">{ad.subtitle}</p>
          <button className="mt-1.5 px-3 py-1 rounded-full text-[10px] sm:text-xs font-bold text-white flex items-center gap-1 active:scale-95 transition-all" style={{ backgroundColor: ad.color || '#2F80ED' }}>
            {ad.cta_text || 'Learn More'} <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Recommended Strip — horizontal scroll ─── */

export function RecommendedStrip({ section = 'colleges' }) {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/ads/section/${section}?format=recommended`)
      .then(res => setAds(res.data || []))
      .catch(() => {});
  }, [section]);

  if (!ads.length) return null;

  return (
    <div className="col-span-2 lg:col-span-3 -mx-1" data-testid="recommended-strip">
      {/* Header */}
      <div className="flex items-center justify-between px-1 mb-2">
        <div className="flex items-center gap-1.5">
          <Star className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-xs font-bold text-[#0A2540]">Recommended For You</span>
          <span className="text-[8px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full uppercase">Sponsored</span>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>

      {/* Horizontal scroll */}
      <div ref={scrollRef} className="flex gap-2.5 overflow-x-auto scrollbar-hide px-1 pb-1 snap-x snap-mandatory">
        {ads.map(ad => (
          <div
            key={ad.ad_id}
            className="flex-shrink-0 w-36 sm:w-44 rounded-xl overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-md transition-all cursor-pointer snap-start"
            onClick={() => navigate(ad.cta_link || '/')}
            data-testid={`rec-card-${ad.ad_id}`}
          >
            <div className="h-20 sm:h-24 relative overflow-hidden">
              <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.background = `linear-gradient(135deg, ${ad.color || '#2F80ED'}, #0A2540)`; }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              {ad.institution_name && (
                <span className="absolute bottom-1 left-1.5 text-[9px] font-bold text-white/90 drop-shadow-sm">{ad.institution_name}</span>
              )}
            </div>
            <div className="p-2">
              <p className="text-[11px] font-bold text-[#0A2540] line-clamp-1">{ad.title}</p>
              <p className="text-[9px] text-slate-400 line-clamp-1 mt-0.5">{ad.subtitle}</p>
              {ad.price_label && (
                <GuestPrice><p className="text-[10px] font-bold mt-1" style={{ color: ad.color || '#2F80ED' }}>{ad.price_label}</p></GuestPrice>
              )}
            </div>
          </div>
        ))}
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

/* ─── In-Feed Ad Card ─── */

export function InFeedAd({ index = 0 }) {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);

  useEffect(() => {
    axios.get(`${API}/ads/section/feed`)
      .then(res => setAds(res.data || []))
      .catch(() => {});
  }, []);

  if (!ads.length) return null;
  const ad = ads[index % ads.length];

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid={`in-feed-ad-${ad.ad_id}`}>
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-50/80 border-b border-slate-100">
        <Megaphone className="h-3 w-3 text-slate-400" />
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Sponsored</span>
      </div>
      <div className="flex items-center gap-3.5 px-4 py-3">
        {ad.logo_url && <img src={ad.logo_url} alt={ad.institution_name} className="w-12 h-12 rounded-xl object-cover border border-slate-100 shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />}
        <div className="flex-1 min-w-0">
          {ad.institution_name && <p className="text-[10px] text-slate-400 font-semibold">{ad.institution_name}</p>}
          <p className="text-sm font-bold text-[#0A2540] leading-tight truncate">{ad.title}</p>
          <p className="text-[11px] text-slate-500 mt-0.5 truncate">{ad.subtitle}</p>
        </div>
      </div>
      <div className="px-4 pb-3">
        <button onClick={() => navigate(ad.cta_link || '/')} className="w-full py-2.5 rounded-xl text-white text-xs font-bold flex items-center justify-center gap-1.5 hover:opacity-90 active:scale-[0.98] transition-all" style={{ backgroundColor: ad.color || '#2F80ED' }}>
          {ad.cta_text || 'Learn More'} <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
